import { Injectable } from '@angular/core';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from 'app/core/triggerbee/triggerbee-api.enum';
import { BehaviorSubject, Observable } from 'rxjs';
import { PatchRequest } from 'app/shared/patch-request.model';
import { Account } from 'app/shared/account/account.model';
import { AccountTypeCustomer } from 'app/back-office/account.model';
import { CustomerModel } from 'app/account-settings/models/customer.model';

@Injectable()
export class CustomersService {
  private readonly logger: Logger;
  private readonly sessionStorageCacheKey: string = 'current-customer';
  public customerInitializedBS = new BehaviorSubject<boolean>(undefined);

  constructor(
    loggerFactory: LoggerFactory,
    private readonly http: HttpRequestDecorator,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
  ) {
    this.logger = loggerFactory.get('CustomersService');
  }

  public loadCurrentCustomer(): void {
    if ((this.currentSiteIdStore.get() ?? 0) > 0) {
      const customerCache = sessionStorage.getItem(this.sessionStorageCacheKey);
      if (customerCache) {
        // Use from cache
        (<any>window).triggerbeeCustomer = JSON.parse(customerCache);
        this.customerInitializedBS.next(true);
      } else {
        // Get from server instead
        this.getCurrent().subscribe(
          (customer) => {
            // When it's successfully we set it as a variable
            (<any>window).triggerbeeCustomer = customer;

            // Add to storage
            sessionStorage.setItem(this.sessionStorageCacheKey, JSON.stringify(customer));
            this.customerInitializedBS.next(true);
          },
          (error) => {
            // 404 (linkage not set up) must still unblock init, not hang the app.
            this.logger.error(`Failed to load current customer (status=${error?.status}). Continuing without it.`);
            this.customerInitializedBS.next(true);
          },
        );
      }
    } else {
      this.customerInitializedBS.next(true);
    }
  }

  public clearCurrentCustomerCache() {
    sessionStorage.removeItem(this.sessionStorageCacheKey);
  }

  public getCurrent(): Observable<any> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/users/current/customer`;
    return this.http.get(url);
  }

  public updateCustomer(id: string, customer: CustomerModel): Observable<CustomerModel> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/customers/${id}`;
    return this.http.put(url, customer);
  }

  public updateCustomerAndAccounts(id: string, customerAccountsProps: any): Observable<any> {
    const body: PatchRequest[] = [];
    customerAccountsProps.forEach((prop) => {
      body.push({
        path: `/${prop.propertyName}`,
        value: prop.value,
        op: 'replace',
      });
    });
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/customers/${id}/accounts`;
    return this.http.patch(url, body);
  }

  public createSelfServiceAccount(customerId: string, request: { domain: string; name: string }): Observable<Account> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(
      `${baseUrl}/customers/${customerId}/accounts?type=${AccountTypeCustomer.SelfService}`,
      request,
    );
  }
}
